<template>
    <div class="card">
        <div class="card-body">
            <div class="d-flex flex-column flex-sm-row justify-content-between mb-4">
                <div class="font-weight-bold align-items-center mb-2 mb-sm-0">
                    Облачные кассы
                    <a target="_blank" :href="getKBLink('crt')" class="btn btn-link text-nowrap ml-2 px-0"><i class="tlm-icon-question-circle" /></a>
                </div>

                <span v-if="stage === 'review'">
                    <small class="text-warning">Модерация занимает от 1 до 3 рабочих дней</small>
                    <span class="badge badge-warning ml-sm-4 text-white">На модерации</span>
                </span>

                <span v-if="stage === 'ready'">
                    <span class="badge badge-success">Модерация пройдена</span>
                </span>

                <span v-if="stage === 'connected'">
                    <span class="badge badge-success">Подключено</span>
                </span>
            </div>

            <template v-if="stage === 'newbe'">
                <p>Для того, чтобы подключить облачные кассы, вам необходимо пройти модерацию через ИНН.</p>

                <ValidationObserver ref="obs" v-slot="{ invalid }" tag="form" class="form" @submit.prevent="verify">
                    <ValidationProvider v-errors.api tag="div" vid="inn" class="form-group" rules="required|numeric" :debounce="500">
                        <div class="d-flex flex-wrap flex-lg-nowrap">
                            <input id="inn" v-model="form.inn" type="text" class="form-control mb-4 mb-lg-0" placeholder="Введите ИНН">
                            <div class="flex-fill ml-lg-3">
                                <button :disabled="invalid || pending" class="btn btn-lg-192 btn-primary" type="submit">Подключить</button>
                            </div>
                        </div>
                    </ValidationProvider>
                </ValidationObserver>
            </template>

            <template v-if="stage === 'review'">
                <div class="d-flex flex-wrap flex-lg-nowrap">
                    <input id="inn" :value="'ИНН: ' + company.inn" type="text" class="form-control mb-4 mb-lg-0" disabled>
                    <button :disabled="invalid || pending" class="btn btn-lg-192 btn-outline-danger ml-lg-3" type="submit" @click.prevent="cancel">Отменить</button>
                </div>
            </template>

            <template v-if="stage === 'ready'">
                <ValidationObserver ref="obs" v-slot="{ invalid }" tag="form" class="form-thin" @submit.prevent="enable">
                    <div class="form-group">
                        <input id="inn" :value="'ИНН: ' + company.inn" type="text" class="form-control" disabled>
                    </div>

                    <div class="row">
                        <ValidationProvider v-errors.api tag="div" vid="tax_mode" class="form-group col-12 col-md-6" rules="required">
                            <select-tax id="tax_mode" v-model="form.tax_mode" class="form-control" />
                        </ValidationProvider>

                        <ValidationProvider v-errors.api vid="vat_base" tag="div" class="form-group col-12 col-md-6" rules="required">
                            <select-vat id="vat_base" v-model="form.vat_base" class="form-control" />
                        </ValidationProvider>
                    </div>

                    <ValidationObserver ref="credentials" tag="div" class="row mb-2 mb-lg-0">
                        <ValidationProvider v-errors.api tag="div" vid="ffd_version" class="form-group col-12 col-md-6" rules="required">
                            <select-ffd-version id="ffd_version" v-model="form.credentials.ffd_version" class="form-control" />
                        </ValidationProvider>

                        <ValidationProvider v-if="form.provider === 'co'" v-errors.api tag="div" vid="group" class="form-group col-12 col-md-6" rules="required">
                            <input id="group" v-model="form.credentials.group" type="text" class="form-control" placeholder="Идентификатор предприятия">
                        </ValidationProvider>

                        <ValidationProvider v-if="form.provider === 'initpro'" v-errors.api tag="div" vid="secret" class="form-group col-12 col-md-6" :rules="{ required: true, regex: /^[\da-zA-Z]{1,32}$/ }">
                            <input id="secret" v-model="form.credentials.secret" type="text" minlength="32" maxlength="32" class="form-control" placeholder="Код группы (авторизационный токен)">
                        </ValidationProvider>
                    </ValidationObserver>

                    <div class="d-flex flex-column flex-lg-row">
                        <div class="align-self-lg-center flex-grow-1 mb-4 mb-md-3 mb-lg-0 d-flex flex-column flex-md-row">
                            <div class="font-weight-bold mr-md-4 mb-2 mb-lg-0">Выберите провайдера:</div>

                            <ValidationProvider v-errors tag="div" vid="provider" class="custom-form-group custom-form-group-provider" rules="required">
                                <div v-for="provider in providers" :key="provider.key" class="custom-control custom-radio d-inline-block">
                                    <input :id="`provider_${provider.key}`" v-model="form.provider" type="radio" class="custom-control-input" name="provider" :value="provider.key">
                                    <label class="custom-control-label" :for="`provider_${provider.key}`">{{ provider.name }}</label>
                                </div>
                            </ValidationProvider>
                        </div>

                        <button :disabled="invalid || pending" class="btn btn-lg-192 btn-primary ml-lg-3" type="submit">Подключить</button>
                    </div>
                </ValidationObserver>
            </template>

            <template v-if="stage === 'connected'">
                <ValidationObserver ref="obs" v-slot="{ invalid, dirty }" tag="form" class="form-thin" @submit.prevent="update">
                    <div class="row">
                        <ValidationProvider v-errors.api tag="div" vid="tax_mode" class="form-group col-12 col-md-6" rules="required">
                            <select-tax id="tax_mode" v-model="form.tax_mode" class="form-control" />
                        </ValidationProvider>

                        <ValidationProvider v-errors.api vid="vat_base" tag="div" class="form-group col-12 col-md-6" rules="required">
                            <select-vat id="vat_base" v-model="form.vat_base" class="form-control" />
                        </ValidationProvider>
                    </div>

                    <div v-if="form.provider === 'co'" class="form-group">
                        <input id="group" :value="'Идентификатор: ' + form.credentials.group" type="text" class="form-control" disabled>
                    </div>

                    <div v-if="form.provider === 'initpro'" class="form-group">
                        <input id="group" :value="'Код группы (авторизационный токен): ' + form.credentials.secret" type="text" class="form-control" disabled>
                    </div>

                    <div class="d-flex flex-column flex-sm-row mt-sm-4">
                        <div class="align-self-sm-center flex-grow-1 font-weight-bold mb-4 mb-sm-0">
                            Провайдер: <span class="text-success ml-3">{{ form.provider | providerName }}</span> <span class="font-weight-normal text-muted">, касса на <strong class="text-secondary">ФФД {{ form.credentials.ffd_version }}</strong></span>
                        </div>

                        <button v-show="dirty" :disabled="invalid || pending" class="btn btn-lg-192 btn-primary ml-sm-3 mb-3 mb-sm-0" type="submit">Сохранить изменения</button>
                        <button :disabled="pending" class="btn btn-lg-192 btn-outline-danger ml-sm-3" type="button" @click.prevent="disable">Отключиться</button>
                    </div>
                </ValidationObserver>
            </template>
        </div>
    </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { mixinApiErrors } from '@/validation'

import SelectTax from '@/components/SelectTax'
import SelectVat from '@/components/SelectVat'
import SelectFfdVersion from '@/components/SelectFfdVersion'

import find from 'lodash/find'

import KBLinksService from '@/services/KBLinksService'

const fillForm = company => ({
    inn: company.inn,

    tax_mode: company.tax_mode,
    vat_base: company.vat_base,
    provider: company.crt && company.crt.provider,

    credentials: {
        ffd_version: (company.crt && company.crt.credentials && company.crt.credentials.ffd_version) ? company.crt.credentials.ffd_version : '1.05',
        group: company.crt && company.crt.credentials && company.crt.credentials.group,
        secret: company.crt && company.crt.credentials && company.crt.credentials.secret
    }
})

const providers = [
    {
        key: 'od',
        name: 'Orange Data'
    },
    {
        key: 'co',
        name: 'Чек онлайн'
    },
    {
        key: 'initpro',
        name: 'Инитпро'
    }
]

const ffdVersions = [
    {
        key: '1.05',
        name: '1.05',
    },
    {
        key: '1.2',
        name: '1.2',
    }
]

export default {
    components: {
        SelectTax,
        SelectVat,
        SelectFfdVersion,
        ValidationObserver,
        ValidationProvider
    },

    filters: {
        providerName(key) {
            const provider = find(providers, { key })

            return provider ? provider.name : key
        }
    },

    props: {
        company: {
            type: Object,
            default: _ => ({})
        },

        pending: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            form: fillForm(this.company)
        }
    },

    computed: {
        stage() {
            let stage = 'newbe' // newbe review ready connected

            if (this.company.inn) {
                stage = 'review'
            }

            if (this.company.is_verified) {
                stage = 'ready'
            }

            if (this.company.crt.provider) {
                stage = 'connected'
            }

            return stage
        },

        providers() {
            return providers
        },

        ffdVersions() {
            return ffdVersions
        }
    },

    watch: {
        company: {
            deep: true,
            handler(company) {
                this.form = fillForm(company)
                this.$refs.obs.reset()
            }
        }
    },

    methods: {
        async verify() {
            if (await this.$refs.obs.validate()) {
                this.$emit('update', { inn: this.form.inn })
            }
        },

        async cancel() {
            this.$emit('update', { inn: null })
        },

        async update() {
            this.$emit('update', { tax_mode: this.form.tax_mode, vat_base: this.form.vat_base })
        },

        async enable() {
            if (await this.$refs.obs.validate()) {
                const { inn, ...attributes } = this.form

                if (attributes.provider !== 'co') {
                    delete attributes.credentials.group
                }

                if (attributes.provider !== 'initpro') {
                    delete attributes.credentials.secret
                }

                this.$emit('enable', attributes)
            }
        },

        async disable() {
            this.$emit('disable')
        },

        mixinApiErrors(errors) {
            mixinApiErrors(this.$refs.obs, errors)
        },

        getKBLink(key) {
            return KBLinksService.get(key)
        }
    }
}
</script>

<style lang="scss">
.custom-form-group-provider {
    .custom-control + .custom-control {
        margin-left: 1.5em;
    }
}
</style>
